import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { apiContractRequest } from '../../../../api/client';

// Owner private brain runtime health. Mongo/Baileys status is read-only here,
// except the deliberate superowner-only lease handoff with two confirmations.
const descriptions = {
  CURRENT_CONNECTED:'Este proceso tiene WhatsApp y ejecuta el motor actualizado.',
  CURRENT_NOT_CONNECTED:'Esta instancia tiene autoridad, pero todavía no abrió el socket.',
  CURRENT_ON_OTHER_INSTANCE:'Otra instancia tiene la misma versión del motor.',
  UNATTESTED_LEGACY_HAS_LEASE:'Otra instancia tiene WhatsApp, pero no declara qué código neuronal ejecuta.',
  OUTDATED_RUNTIME_HAS_LEASE:'Otra instancia tiene una versión distinta del motor neuronal.',
  NO_ACTIVE_LEASE:'No se encontró ninguna instancia con un lease activo de WhatsApp.',
  NOT_CONFIGURED:'WhatsApp Owner no está configurado.',
};
const short = value => value ? String(value).slice(0, 12) : 'Sin huella';

export default function OwnerRuntimeAuthority() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const reload = useCallback(async () => {
    try {
      const data = await apiContractRequest('/api/owner/whatsapp/baileys/status', { retryAttempts:0 });
      setStatus(data?.runtimeAuthority || null);
      setError(!data?.runtimeAuthority ? 'Este backend todavía no expone identidad de runtime.' : '');
    } catch (err) { setError(err?.message || 'No se pudo consultar la autoridad de Owner.'); }
  }, []);
  useEffect(() => {
    reload();
    const interval=setInterval(reload, 15000);
    return () => clearInterval(interval);
  }, [reload]);
  const handoff=async () => {
    if (!status?.requiresHandoff || busy) return;
    if (!window.confirm('El lease de WhatsApp parece estar en una instancia anterior. ¿Querés traspasarlo a este backend? Se conserva la sesión y no se reenvían mensajes, pero puede haber una interrupción breve.')) return;
    if (!window.confirm('Última confirmación: se cerrará la autoridad del socket anterior. Hacelo una sola vez y después revisá los estados.')) return;
    setBusy(true);setNotice('');
    try {
      const result=await apiContractRequest('/api/owner/whatsapp/baileys/runtime/handoff', {
        method:'POST', body:{confirm:'FENCE_LEGACY_OWNER_RUNTIME'}, retryAttempts:0,
      });
      setNotice(result?.reason==='HANDOFF_SCHEDULED' ? 'Traspaso solicitado. El nuevo backend abrirá WhatsApp después de que la instancia anterior cierre. Revisá el estado antes de probar.' : `Resultado: ${result?.reason || 'Sin detalle'}`);
      await reload();
    } catch(err) { setError(err?.message || 'No fue posible traspasar el lease; no lo fuerces.'); }
    finally { setBusy(false); }
  };
  const isHealthy=status?.status==='CURRENT_CONNECTED';
  const incompatible=status?.requiresHandoff;
  return <Box sx={{ mb:2, p:1.5, border:'1px solid', borderColor:'divider', borderRadius:2 }}>
    <Stack direction={{xs:'column',sm:'row'}} justifyContent="space-between" alignItems={{sm:'center'}} gap={1}>
      <Box>
        <Typography fontWeight={750} variant="subtitle2">Runtime neuronal · autoridad de WhatsApp</Typography>
        <Typography variant="caption" color="text.secondary">{descriptions[status?.status] || 'Identidad del proceso sin verificar.'}</Typography>
      </Box>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" color={isHealthy?'success':incompatible?'error':'warning'} label={status?.status || 'SIN DIAGNÓSTICO'} />
        <Button size="small" onClick={reload} variant="outlined">Actualizar</Button>
        {incompatible && <Button size="small" color="warning" disabled={busy} variant="contained" onClick={handoff}>{busy?<CircularProgress size={15}/>: 'Traspasar runtime anterior'}</Button>}
      </Stack>
    </Stack>
    {status?.localRuntime && <Typography variant="caption" sx={{display:'block', mt:.5}}>Código local: {short(status.localRuntime.fingerprint)} · Lease: {short(status.lease?.fingerprint)} · Activo: {status.lease?.active?'sí':'no'}</Typography>}
    {error && <Alert severity="warning" sx={{mt:1}}>{error}</Alert>}
    {notice && <Alert severity="info" sx={{mt:1}}>{notice}</Alert>}
  </Box>;
}
